// ============================================================================
// TASK OVERVIEW TAB
// Purpose-built overview for item_type === 'task'.
// Shows: progress & effort, parent context, basic info, contact, dependencies,
//        and inline subtasks.
// Date: March 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Target, Clock, Zap, PlayCircle, TrendingUp, Calendar, FileText,
  Building2, MapPin, Search, ChevronDown,
  ArrowRight, Link2, AlertTriangle, ExternalLink, Briefcase, Flag,
  Navigation, Copy, Check, Hand
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import InlineSubtasksSection from '../work-items/InlineSubtasksSection';

// ── Types ──

interface TaskOverviewTabProps {
  workItem: WorkItem;
  formData: Record<string, any>;
  onDataChange: (field: string, value: any) => void;
  isDark?: boolean;
  jobTypes?: Array<{ key: string; label: string; icon?: string; color?: string }>;
  workItemId?: string;
  workItemType?: string;
  onOpenItem?: (itemId: string) => void;
  onOpenSubtask?: (itemId: string) => void;
  onSubtasksCountChange?: (count: number) => void;
}

interface Contact {
  id: number;
  company_name: string | null;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  contact_type: string;
}

interface Location {
  id: number;
  contact_id: number;
  location_name: string;
  address_line1: string | null;
  city: string | null;
  is_primary: boolean;
}

interface RelatedItem {
  id: string;
  item_number: string;
  title: string;
  item_type: string;
  status: string;
  relationship_type: string;
}

interface SourceContextEntry {
  key: string;
  label: string;
  value: string;
}

interface TaskSourceReference {
  type: string | null;
  id: string | null;
  url: string | null;
  contextEntries: SourceContextEntry[];
}

// ── Helpers ──

const getToken = () => authService.getTokens()?.access_token || '';
const WORK_ITEM_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SOURCE_CONTEXT_EXCLUDED_KEYS = new Set([
  'source_reference_type',
  'source_reference_id',
  'source_reference_url',
  'source_reference_key',
  'quick_add_source',
  'migrated_from_user_task_id',
  'migrated_from_user_tasks_table',
  'legacy_reference_type',
  'legacy_reference_id',
  'legacy_reference_url',
  'legacy_user_id',
  'legacy_assigned_to_user_id',
]);
const SOURCE_CONTEXT_PRIORITY_KEYS = [
  'doc_type',
  'doc_number',
  'item_type',
  'item_number',
  'status',
  'current_status',
  'priority',
  'progress',
  'contact_name',
  'grand_total',
  'legacy_user_task_status',
  'legacy_recurrence_pattern',
  'legacy_recurrence_end_date',
];

const isUuidLike = (value: unknown): value is string => WORK_ITEM_UUID_REGEX.test(String(value || ''));

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const parseRecord = (value: unknown): Record<string, unknown> => {
  if (isRecord(value)) {
    return value;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  return {};
};

const toLabel = (value: string): string => {
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const toContextDisplayValue = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }

    const flattened = value
      .map((entry) => toContextDisplayValue(entry))
      .filter((entry): entry is string => Boolean(entry));

    return flattened.length > 0 ? flattened.join(', ') : null;
  }

  if (isRecord(value)) {
    try {
      return JSON.stringify(value);
    } catch {
      return '[object]';
    }
  }

  return String(value);
};

const buildTaskSourceReference = (customFields: Record<string, unknown>): TaskSourceReference => {
  const sourceType = typeof customFields.source_reference_type === 'string'
    ? customFields.source_reference_type
    : typeof customFields.legacy_reference_type === 'string'
      ? customFields.legacy_reference_type
      : null;

  const sourceIdRaw = customFields.source_reference_id ?? customFields.legacy_reference_id;
  const sourceId = typeof sourceIdRaw === 'string' || typeof sourceIdRaw === 'number'
    ? String(sourceIdRaw)
    : null;

  const sourceUrlRaw = customFields.source_reference_url ?? customFields.legacy_reference_url;
  const sourceUrl = typeof sourceUrlRaw === 'string' && sourceUrlRaw.trim().length > 0
    ? sourceUrlRaw.trim()
    : null;

  const prioritizedEntries = SOURCE_CONTEXT_PRIORITY_KEYS
    .map((key) => {
      if (SOURCE_CONTEXT_EXCLUDED_KEYS.has(key)) {
        return null;
      }

      const displayValue = toContextDisplayValue(customFields[key]);
      return displayValue
        ? { key, label: toLabel(key), value: displayValue }
        : null;
    })
    .filter((entry): entry is SourceContextEntry => Boolean(entry));

  const additionalEntries = Object.entries(customFields)
    .filter(([key]) => !SOURCE_CONTEXT_EXCLUDED_KEYS.has(key) && !SOURCE_CONTEXT_PRIORITY_KEYS.includes(key))
    .map(([key, value]) => {
      const displayValue = toContextDisplayValue(value);
      return displayValue
        ? { key, label: toLabel(key), value: displayValue }
        : null;
    })
    .filter((entry): entry is SourceContextEntry => Boolean(entry));

  return {
    type: sourceType,
    id: sourceId,
    url: sourceUrl,
    contextEntries: [...prioritizedEntries, ...additionalEntries],
  };
};

const getRelTypeLabel = (type: string): string => {
  const map: Record<string, string> = {
    'child_of': 'Part of',
    'parent-child': 'Parent',
    'parent_child': 'Parent',
    'depends-on': 'Depends on',
    'depends_on': 'Depends on',
    'blocks': 'Blocks',
    'related': 'Related to',
  };
  return map[type] || type.replace(/[_-]/g, ' ');
};

const getItemTypeBadge = (type: string, isDark: boolean): string => {
  const map: Record<string, string> = {
    project: isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700',
    job: isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700',
    task: isDark ? 'bg-cyan-900/30 text-cyan-400' : 'bg-cyan-100 text-cyan-700',
    appointment: isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700',
  };
  return map[type] || (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700');
};

// ── Component ──

export default function TaskOverviewTab({
  workItem, formData, onDataChange, isDark: isDarkProp,
  jobTypes: _jobTypes = [], workItemId, workItemType,
  onOpenItem, onOpenSubtask, onSubtasksCountChange
}: TaskOverviewTabProps) {
  const { currentTheme } = useTheme();
  const isDark = isDarkProp ?? currentTheme.mode === 'dark';
  const effectiveId = workItemId || workItem.id;
  const effectiveType = workItemType || workItem.item_type;

  // Time entries
  const [timeEntriesTotal, setTimeEntriesTotal] = useState<number | null>(null);
  const [_loadingTimeEntries, setLoadingTimeEntries] = useState(false);
  const [travelMinutes, setTravelMinutes] = useState(0);
  const [totalDistanceKm, setTotalDistanceKm] = useState(0);

  // Relationships / parent / dependencies
  const [parentItem, setParentItem] = useState<RelatedItem | null>(null);
  const [dependencies, setDependencies] = useState<RelatedItem[]>([]);
  const [blockers, setBlockers] = useState<RelatedItem[]>([]);
  const [_loadingRelationships, setLoadingRelationships] = useState(false);

  // Contact state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactSearch, setContactSearch] = useState('');
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const contactRef = useRef<HTMLDivElement>(null);

  // Locations
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const mergedCustomFields = useMemo(() => {
    return {
      ...parseRecord(workItem.custom_fields),
      ...parseRecord(formData.custom_fields),
    };
  }, [formData.custom_fields, workItem.custom_fields]);

  // Agent "needs you" payload — personas flag manual steps (run a script in
  // pgAdmin, approve, supply info) via custom_fields.agent.action_required.
  const agentFields = useMemo(() => parseRecord(mergedCustomFields['agent']), [mergedCustomFields]);
  const actionRequired = useMemo(() => parseRecord(agentFields['action_required']), [agentFields]);
  const agentStatus = typeof agentFields['status'] === 'string' ? agentFields['status'] : '';
  const actionKind = typeof actionRequired['kind'] === 'string' ? actionRequired['kind'] : '';
  const actionDetail = typeof actionRequired['detail'] === 'string' ? actionRequired['detail'] : '';
  const actionUrl = typeof actionRequired['url'] === 'string' ? actionRequired['url'] : '';
  const actionScript = typeof actionRequired['script'] === 'string' ? actionRequired['script'] : '';
  const agentPersona = typeof agentFields['persona'] === 'string' ? agentFields['persona'] : '';
  const hasAgentAction = Boolean(
    (agentStatus === 'awaiting_human' || actionKind || actionDetail) &&
    (actionDetail || actionUrl || actionScript)
  );
  const [copiedScript, setCopiedScript] = useState(false);
  const copyScript = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedScript(true);
      window.setTimeout(() => setCopiedScript(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const sourceReference = useMemo(() => buildTaskSourceReference(mergedCustomFields), [mergedCustomFields]);
  const hasSourceContext = Boolean(sourceReference.type || sourceReference.id || sourceReference.url || sourceReference.contextEntries.length > 0);
  const canOpenSourceWorkItem = Boolean(
    sourceReference.type === 'work_item'
    && sourceReference.id
    && isUuidLike(sourceReference.id)
    && sourceReference.id !== parentItem?.id
  );

  // ── Fetch Time Entries ──
  useEffect(() => {
    if (!effectiveId) return;
    (async () => {
      setLoadingTimeEntries(true);
      try {
        const res = await fetch(`/api/v1/work-items/${effectiveId}/time-entries/summary`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data) {
            const hrs = data.data.total_hours || 0;
            setTimeEntriesTotal(hrs);
            setTravelMinutes(data.data.travel_minutes || 0);
            setTotalDistanceKm(data.data.total_distance_km || 0);
            if (!formData.actual_hours || formData.actual_hours === 0) {
              onDataChange('actual_hours', hrs);
            }
          }
        }
      } catch {} finally { setLoadingTimeEntries(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load summary when item changes; avoid re-sync loops from formData/onDataChange
  }, [effectiveId]);

  // ── Fetch Relationships (parent + dependencies) ──
  useEffect(() => {
    if (!effectiveId) return;
    (async () => {
      setLoadingRelationships(true);
      try {
        const res = await fetch(`/api/v1/work-items/${effectiveId}/relationships`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            // API returns { relationships: { outgoing: { type: [...] }, incoming: { type: [...] } }, summary: { parent } }
            const outgoing = data.data?.relationships?.outgoing || {};
            const incoming = data.data?.relationships?.incoming || {};

            // Find parent via outgoing child_of, or summary.parent
            const childOfRels = outgoing['child_of'] || [];
            if (childOfRels.length > 0) {
              const p = childOfRels[0].item;
              setParentItem({
                id: p.id, item_number: p.item_number || '', title: p.title || '',
                item_type: p.item_type || '', status: p.status || '', relationship_type: 'child_of',
              });
            } else if (data.data?.summary?.parent) {
              try {
                const parentRes = await fetch(`/api/v1/work-items/${data.data.summary.parent}`, {
                  headers: { Authorization: `Bearer ${getToken()}` }
                });
                if (parentRes.ok) {
                  const parentData = await parentRes.json();
                  if (parentData.success && parentData.data) {
                    const p = parentData.data;
                    setParentItem({
                      id: p.id, item_number: p.item_number, title: p.title,
                      item_type: p.item_type, status: p.status, relationship_type: 'child_of'
                    });
                  }
                }
              } catch {}
            }

            // Collect dependencies (outgoing depends_on)
            const dependsOnRels = (outgoing['depends_on'] || []) as Array<{ item?: Partial<RelatedItem> }>;
            setDependencies(dependsOnRels.map((r) => ({
              id: r.item?.id || '', item_number: r.item?.item_number || '', title: r.item?.title || '',
              item_type: r.item?.item_type || '', status: r.item?.status || '', relationship_type: 'depends_on',
            })));

            // Collect blockers (outgoing blocks)
            const blocksRels = (outgoing['blocks'] || []) as Array<{ item?: Partial<RelatedItem> }>;
            setBlockers(blocksRels.map((r) => ({
              id: r.item?.id || '', item_number: r.item?.item_number || '', title: r.item?.title || '',
              item_type: r.item?.item_type || '', status: r.item?.status || '', relationship_type: 'blocks',
            })));

            // Also check incoming blocked_by as dependencies
            const blockedByRels = (incoming['blocks'] || []) as Array<{ item?: Partial<RelatedItem> }>;
            if (blockedByRels.length > 0) {
              setDependencies(prev => [...prev, ...blockedByRels.map((r) => ({
                id: r.item?.id || '', item_number: r.item?.item_number || '', title: r.item?.title || '',
                item_type: r.item?.item_type || '', status: r.item?.status || '', relationship_type: 'blocked_by',
              }))]);
            }
          }
        }
      } catch (err) { console.error('Error loading relationships:', err); }
      finally { setLoadingRelationships(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveId]);

  // ── Load Contacts ──
  useEffect(() => {
    (async () => {
      setLoadingContacts(true);
      try {
        const res = await fetch('/api/v1/contacts?contact_types=customer,supplier&limit=200', {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        if (res.ok) { const d = await res.json(); if (d.success) setContacts(d.data || []); }
      } catch {} finally { setLoadingContacts(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load Locations when contact changes ──
  useEffect(() => {
    if (!formData.contact_id) { setLocations([]); return; }
    (async () => {
      setLoadingLocations(true);
      try {
        const res = await fetch(`/api/v1/contacts/${formData.contact_id}/locations`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        if (res.ok) { const d = await res.json(); if (d.success) setLocations(d.data || []); }
      } catch {} finally { setLoadingLocations(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.contact_id]);

  // Click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (contactRef.current && !contactRef.current.contains(e.target as Node)) setIsContactOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleContactChange = (contactId: number | null, contactName?: string) => {
    onDataChange('contact_id', contactId);
    onDataChange('contact_name', contactName || null);
    onDataChange('location_id', null);
    onDataChange('location_name', null);
  };

  const handleLocationChange = (locationId: number | null) => {
    const loc = locations.find(l => l.id == locationId);
    onDataChange('location_id', locationId);
    onDataChange('location_name', loc?.location_name || null);
  };

  const selectedContact = contacts.find(c => c.id == formData.contact_id);
  const filteredContacts = contacts.filter(c => {
    const name = `${c.company_name || ''} ${c.contact_person || ''} ${c.email || ''}`.toLowerCase();
    return name.includes(contactSearch.toLowerCase());
  });

  // ── Effort calculations ──
  const actualHours = timeEntriesTotal || formData.actual_hours || 0;
  const estimatedHours = formData.estimated_hours || 0;
  const diff = estimatedHours - actualHours;
  const isSurplus = diff >= 0;
  const efficiency = estimatedHours > 0 && actualHours > 0 ? Math.round((estimatedHours / actualHours) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* ═══════════════════ AGENT ACTION REQUIRED ═══════════════════ */}
      {hasAgentAction && (
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-orange-950/30 border-orange-900/50' : 'bg-orange-50 border-orange-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 flex-wrap ${isDark ? 'text-orange-200' : 'text-orange-800'}`}>
            <Hand className="w-4 h-4" />
            Action required
            {actionKind && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide ${isDark ? 'bg-orange-900/60 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                {actionKind.replace(/_/g, ' ')}
              </span>
            )}
            {agentPersona && (
              <span className={`text-[10px] ${isDark ? 'text-orange-400/70' : 'text-orange-600/70'}`}>
                flagged by {agentPersona}
              </span>
            )}
          </h3>

          {actionDetail && (
            <p className={`text-sm whitespace-pre-wrap break-words mb-3 ${isDark ? 'text-orange-100' : 'text-orange-900'}`}>
              {actionDetail}
            </p>
          )}

          {actionUrl && (
            <a
              href={actionUrl}
              {...(actionUrl.startsWith('/') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 mb-3 text-xs font-medium rounded-lg transition-colors ${isDark ? 'bg-orange-900/60 text-orange-200 hover:bg-orange-900' : 'bg-white text-orange-700 hover:bg-orange-100 border border-orange-200'}`}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open link
              <span className={`font-mono text-[10px] ${isDark ? 'text-orange-400/80' : 'text-orange-500'}`}>{actionUrl}</span>
            </a>
          )}

          {actionScript && (
            <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-orange-900/60' : 'border-orange-200'}`}>
              <div className={`flex items-center justify-between px-3 py-1.5 ${isDark ? 'bg-orange-950/60' : 'bg-orange-100/70'}`}>
                <span className={`text-[10px] font-medium uppercase tracking-wide ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>Script</span>
                <button
                  type="button"
                  onClick={() => void copyScript(actionScript)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${isDark ? 'text-orange-300 hover:bg-orange-900/60' : 'text-orange-700 hover:bg-orange-200/60'}`}
                >
                  {copiedScript ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedScript ? 'Copied' : 'Copy'}
                </button>
              </div>
              <pre className={`p-3 text-xs font-mono whitespace-pre-wrap break-words max-h-64 overflow-y-auto ${isDark ? 'bg-gray-950 text-orange-100' : 'bg-white text-gray-800'}`}>
                {actionScript}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════ PROGRESS & EFFORT ═══════════════════ */}
      <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-base font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          <Target className="w-4 h-4 text-blue-500" />
          Progress & Effort
        </h3>
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Progress Ring */}
          <div className="flex-shrink-0 flex flex-col items-center">
            <div className="relative w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle cx="48" cy="48" r="40" stroke={isDark ? '#374151' : '#e5e7eb'} strokeWidth="8" fill="none" />
                <circle cx="48" cy="48" r="40"
                  stroke={
                    (formData.progress_percentage || 0) === 100 ? '#10b981' :
                    (formData.progress_percentage || 0) >= 75 ? '#3b82f6' :
                    (formData.progress_percentage || 0) >= 50 ? '#f59e0b' :
                    (formData.progress_percentage || 0) >= 25 ? '#f97316' : '#ef4444'
                  }
                  strokeWidth="8" fill="none" strokeLinecap="round"
                  strokeDasharray={`${(formData.progress_percentage || 0) * 2.51} 251`}
                  className="transition-all duration-500 ease-out" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formData.progress_percentage || 0}%</span>
              </div>
            </div>
            <div className="flex gap-1 mt-2">
              {[0, 25, 50, 75, 100].map(val => (
                <button key={val} onClick={() => onDataChange('progress_percentage', val)}
                  className={`px-1.5 py-0.5 text-xs rounded transition-all ${
                    (formData.progress_percentage || 0) === val
                      ? 'bg-blue-500 text-white'
                      : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>{val}%</button>
              ))}
            </div>
          </div>

          {/* Stats Grid */}
          <div className={`flex-1 grid grid-cols-2 ${travelMinutes > 0 || totalDistanceKm > 0 ? 'lg:grid-cols-6' : 'lg:grid-cols-4'} gap-2`}>
            {/* Complexity */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-purple-50'} border ${isDark ? 'border-gray-700' : 'border-purple-100'}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <Zap className={`w-3.5 h-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Complexity</span>
              </div>
              <select value={formData.complexity_points || ''} onChange={(e) => onDataChange('complexity_points', e.target.value ? parseInt(e.target.value) : null)}
                className={`text-lg font-bold bg-transparent border-none focus:ring-0 p-0 w-full ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                <option value="">-</option>
                {[1, 2, 3, 5, 8, 13, 21].map(pt => <option key={pt} value={pt}>{pt} pts</option>)}
              </select>
            </div>
            {/* Estimated */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-blue-50'} border ${isDark ? 'border-gray-700' : 'border-blue-100'}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Estimated</span>
              </div>
              <div className="flex items-baseline gap-1">
                <input type="number" step="0.5" min="0" value={formData.estimated_hours || ''}
                  onChange={(e) => onDataChange('estimated_hours', e.target.value ? parseFloat(e.target.value) : null)}
                  className={`text-lg font-bold bg-transparent border-none focus:ring-0 w-12 p-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} placeholder="0" />
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>hrs</span>
              </div>
            </div>
            {/* Actual */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-green-50'} border ${isDark ? 'border-gray-700' : 'border-green-100'}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <PlayCircle className={`w-3.5 h-3.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Actual</span>
                {timeEntriesTotal !== null && timeEntriesTotal > 0 && (
                  <span className={`text-xs ${isDark ? 'text-green-500' : 'text-green-600'}`} title="From WIP timer">⏱</span>
                )}
              </div>
              {timeEntriesTotal !== null && timeEntriesTotal > 0 ? (
                <>
                  <div className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>{timeEntriesTotal.toFixed(2)} hrs</div>
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>From time entries</div>
                </>
              ) : (
                <>
                  <div className={`text-lg font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>0 hrs</div>
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No WIP entries yet</div>
                </>
              )}
            </div>
            {/* Surplus / Shortfall */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-amber-50'} border ${isDark ? 'border-gray-700' : 'border-amber-100'}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className={`w-3.5 h-3.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Surplus / Shortfall</span>
              </div>
              {estimatedHours > 0 && actualHours > 0 ? (
                <div>
                  <div className={`text-lg font-bold ${isSurplus ? isDark ? 'text-green-400' : 'text-green-600' : isDark ? 'text-red-400' : 'text-red-600'}`}>
                    {isSurplus ? '+' : '-'}{Math.abs(diff).toFixed(1)}h
                  </div>
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {isSurplus ? 'Under budget' : 'Over budget'} ({efficiency}%)
                  </div>
                </div>
              ) : (
                <div className={`text-lg font-bold ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>—</div>
              )}
            </div>
            {/* Travel Time */}
            {travelMinutes > 0 && (
              <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-purple-50'} border ${isDark ? 'border-gray-700' : 'border-purple-100'}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Navigation className={`w-3.5 h-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Travel</span>
                </div>
                <div className={`text-lg font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                  {travelMinutes >= 60 ? `${(travelMinutes / 60).toFixed(1)}h` : `${travelMinutes}m`}
                </div>
              </div>
            )}
            {/* Distance */}
            {totalDistanceKm > 0 && (
              <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-indigo-50'} border ${isDark ? 'border-gray-700' : 'border-indigo-100'}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <MapPin className={`w-3.5 h-3.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Distance</span>
                </div>
                <div className={`text-lg font-bold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>{totalDistanceKm.toFixed(1)} km</div>
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className={`relative h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${
              (formData.progress_percentage || 0) === 100 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
              (formData.progress_percentage || 0) >= 75 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
              (formData.progress_percentage || 0) >= 50 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
              (formData.progress_percentage || 0) >= 25 ? 'bg-gradient-to-r from-orange-500 to-amber-500' :
              'bg-gradient-to-r from-red-500 to-orange-500'
            }`} style={{ width: `${formData.progress_percentage || 0}%` }} />
          </div>
          <div className="flex justify-between mt-1 text-xs">
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Start</span>
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Done</span>
          </div>
        </div>
      </div>

      {/* ═══════════════════ PARENT CONTEXT ═══════════════════ */}
      {parentItem && (
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg flex-shrink-0 ${
              parentItem.item_type === 'project'
                ? isDark ? 'bg-purple-900/30' : 'bg-purple-100'
                : isDark ? 'bg-blue-900/30' : 'bg-blue-100'
            }`}>
              {parentItem.item_type === 'project' ? (
                <Briefcase className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              ) : (
                <Briefcase className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {getRelTypeLabel(parentItem.relationship_type)}
              </div>
              <button onClick={() => onOpenItem?.(parentItem.id)}
                className={`text-sm font-medium hover:text-blue-500 transition-colors truncate block ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {parentItem.item_number} — {parentItem.title}
              </button>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getItemTypeBadge(parentItem.item_type, isDark)}`}>
                  {parentItem.item_type.replace(/_/g, ' ')}
                </span>
                <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {parentItem.status.replace(/_/g, ' ')}
                </span>
              </div>
            </div>
            <button onClick={() => onOpenItem?.(parentItem.id)}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════ SOURCE CONTEXT ═══════════════════ */}
      {hasSourceContext && (
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <Link2 className="w-4 h-4 text-violet-500" />
            Source Context
          </h3>

          <div className="space-y-3">
            {(sourceReference.type || sourceReference.id || sourceReference.url) && (
              <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-violet-50 border-violet-100'}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {sourceReference.type && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getItemTypeBadge(sourceReference.type, isDark)}`}>
                          {sourceReference.type.replace(/_/g, ' ')}
                        </span>
                      )}
                      {sourceReference.id && (
                        <span className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          #{sourceReference.id}
                        </span>
                      )}
                    </div>

                    {sourceReference.url && (
                      <div className={`text-xs break-all ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {sourceReference.url}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {canOpenSourceWorkItem && sourceReference.id && (
                      <button
                        type="button"
                        onClick={() => onOpenItem?.(sourceReference.id!)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open source item
                      </button>
                    )}

                    {!canOpenSourceWorkItem && sourceReference.url && (
                      <a
                        href={sourceReference.url}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open source
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {sourceReference.contextEntries.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                {sourceReference.contextEntries.map((entry) => (
                  <div key={entry.key} className={`p-3 rounded-lg border ${isDark ? 'bg-gray-900/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className={`text-[11px] font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {entry.label}
                    </div>
                    <div className={`text-sm break-words ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {entry.value}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════ BASIC INFO + CONTACT ═══════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Basic Information */}
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <FileText className="w-4 h-4" />
            Task Details
          </h3>
          <div className="space-y-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Title <span className="text-red-500">*</span>
              </label>
              <input type="text" value={formData.title || ''} onChange={(e) => onDataChange('title', e.target.value)}
                className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="Task title" />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
              <textarea value={formData.description || ''} onChange={(e) => onDataChange('description', e.target.value)} rows={5}
                className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 resize-y min-h-[100px] ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="What needs to be done" />
            </div>
            {/* Due Date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <Calendar className="w-3 h-3 inline mr-1" />Start Date
                </label>
                <input type="date" value={formData.start_date ? formData.start_date.substring(0, 10) : ''}
                  onChange={(e) => onDataChange('start_date', e.target.value || null)}
                  className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <Flag className="w-3 h-3 inline mr-1" />Due Date
                </label>
                <input type="date" value={formData.due_date ? formData.due_date.substring(0, 10) : ''}
                  onChange={(e) => onDataChange('due_date', e.target.value || null)}
                  className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`} />
              </div>
            </div>
            {/* Tags */}
            {workItem.tags && workItem.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {workItem.tags.map((tag: string, i: number) => (
                  <span key={i} className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Contact & Location */}
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <Building2 className="w-4 h-4" />
            Contact
          </h3>

          {/* Contact Selector */}
          <div className="relative mb-3" ref={contactRef}>
            <button type="button" onClick={() => setIsContactOpen(!isContactOpen)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className={`truncate ${!selectedContact ? 'text-gray-400' : ''}`}>
                  {selectedContact ? (selectedContact.company_name || selectedContact.contact_person) : 'Select contact...'}
                </span>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${isContactOpen ? 'rotate-180' : ''}`} />
            </button>
            {isContactOpen && (
              <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input type="text" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Search contacts..."
                      className={`w-full pl-7 pr-2 py-1 text-sm rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200'}`} autoFocus />
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {loadingContacts ? <div className="p-2 text-center text-gray-500 text-xs">Loading...</div> :
                  filteredContacts.length === 0 ? <div className="p-2 text-center text-gray-500 text-xs">No contacts found</div> :
                  filteredContacts.map(c => (
                    <button key={c.id} type="button"
                      onClick={() => { handleContactChange(c.id, c.company_name || c.contact_person || undefined); setIsContactOpen(false); setContactSearch(''); }}
                      className={`w-full flex items-center gap-2 p-2 text-left text-sm ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} ${formData.contact_id == c.id ? (isDark ? 'bg-gray-700' : 'bg-blue-50') : ''}`}>
                      <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{c.company_name || c.contact_person || 'Unnamed'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Contact Display */}
          {selectedContact && (
            <div className={`p-3 rounded-lg mb-3 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                {selectedContact.company_name || selectedContact.contact_person}
              </div>
              {selectedContact.email && (
                <div className={`flex items-center gap-1 text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>📧</span> {selectedContact.email}
                </div>
              )}
              {selectedContact.phone && (
                <div className={`flex items-center gap-1 text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>📞</span> {selectedContact.phone}
                </div>
              )}
            </div>
          )}

          {/* Location */}
          {formData.contact_id && (
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <MapPin className="w-3 h-3 inline mr-1" />Location
              </label>
              {loadingLocations ? (
                <div className={`text-xs p-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading locations...</div>
              ) : locations.length === 0 ? (
                <div className={`text-xs p-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No locations on file</div>
              ) : (
                <select value={formData.location_id || ''}
                  onChange={(e) => handleLocationChange(e.target.value ? parseInt(e.target.value) : null)}
                  className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}>
                  <option value="">Select location...</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.location_name}{l.is_primary ? ' ★' : ''}{l.address_line1 ? ` — ${l.address_line1}` : ''}{l.city ? `, ${l.city}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════ DEPENDENCIES ═══════════════════ */}
      {(dependencies.length > 0 || blockers.length > 0 || workItem.blocked) && (
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <Link2 className="w-4 h-4 text-orange-500" />
            Dependencies
          </h3>

          {/* Blocked Warning */}
          {workItem.blocked && (
            <div className={`mb-3 p-3 rounded-lg border flex items-start gap-2 ${isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
              <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
              <div>
                <div className={`text-sm font-medium ${isDark ? 'text-red-200' : 'text-red-900'}`}>This task is blocked</div>
                {workItem.blocked_reason && (
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-red-300' : 'text-red-700'}`}>{workItem.blocked_reason}</div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            {/* Dependencies (this task depends on) */}
            {dependencies.map(dep => (
              <div key={dep.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                <ArrowRight className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-xs ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>Depends on</div>
                  <button onClick={() => onOpenItem?.(dep.id)}
                    className={`text-sm font-medium hover:text-blue-500 transition-colors truncate block ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {dep.item_number} — {dep.title}
                  </button>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getItemTypeBadge(dep.item_type, isDark)}`}>
                  {dep.status.replace(/_/g, ' ')}
                </span>
              </div>
            ))}

            {/* Blockers (this task blocks) */}
            {blockers.map(blk => (
              <div key={blk.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                <ArrowRight className={`w-4 h-4 flex-shrink-0 transform rotate-180 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-xs ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>Blocks</div>
                  <button onClick={() => onOpenItem?.(blk.id)}
                    className={`text-sm font-medium hover:text-blue-500 transition-colors truncate block ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {blk.item_number} — {blk.title}
                  </button>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getItemTypeBadge(blk.item_type, isDark)}`}>
                  {blk.status.replace(/_/g, ' ')}
                </span>
              </div>
            ))}

            {dependencies.length === 0 && blockers.length === 0 && !workItem.blocked && (
              <div className={`text-xs text-center py-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No dependency relationships found
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════ SUBTASKS ═══════════════════ */}
      <InlineSubtasksSection
        workItemId={effectiveId}
        workItemType={effectiveType}
        isDark={isDark}
        onOpenItem={onOpenItem}
        onOpenSubtask={onOpenSubtask}
        onSubtasksCountChange={onSubtasksCountChange}
      />
    </div>
  );
}
